import { ConfigOptions, mkConfig } from 'export-to-csv';

type CsvFormat = {
    labels: {
        download: string;
        export: string;
    };
    fieldSeparator: ',' | ';';
    decimalSeparator: '.' | ',';
};

// Excel in comma-decimal locales misreads `0.5` as `5`, so those users need the semicolon variant.
const CSV_FORMATS = {
    comma: {
        labels: { download: 'Download CSV (,)', export: 'Export as CSV (,)' },
        fieldSeparator: ',',
        decimalSeparator: '.',
    },
    semicolon: {
        labels: { download: 'Download CSV (;)', export: 'Export as CSV (;)' },
        fieldSeparator: ';',
        decimalSeparator: ',',
    },
} satisfies Record<string, CsvFormat>;

const CsvHelpers = {
    config: ({ fieldSeparator, decimalSeparator }: CsvFormat, options: ConfigOptions) =>
        mkConfig({ ...options, fieldSeparator, decimalSeparator }),

    transform: (csv: string, target: CsvFormat) => {
        const source = detectFormat(csv);

        return csv
            .split(/\r?\n/)
            .map((line) =>
                splitCells(line, source.fieldSeparator)
                    .map((cell) => transformCell(cell, source, target))
                    .join(target.fieldSeparator),
            )
            .join('\n');
    },
};

const detectFormat = (csv: string): CsvFormat => {
    const [header = ''] = csv.split(/\r?\n/, 1);

    return splitCells(header, ';').length > splitCells(header, ',').length ? CSV_FORMATS.semicolon : CSV_FORMATS.comma;
};

const transformCell = (cell: string, source: CsvFormat, target: CsvFormat) => {
    const value = cell.startsWith('"') && cell.endsWith('"') ? cell.slice(1, -1).replaceAll('""', '"') : cell;

    if (DECIMAL_PATTERNS[source.decimalSeparator].test(value)) {
        return value.replace(source.decimalSeparator, target.decimalSeparator);
    }

    if (cell.includes(target.fieldSeparator) && !cell.startsWith('"')) {
        return `"${cell.replaceAll('"', '""')}"`;
    }

    return cell;
};

const DECIMAL_PATTERNS: Record<CsvFormat['decimalSeparator'], RegExp> = {
    '.': /^\s*-?\d*\.\d+(e[+-]?\d+)?\s*$/i,
    ',': /^\s*-?\d*,\d+(e[+-]?\d+)?\s*$/i,
};

const splitCells = (line: string, separator: CsvFormat['fieldSeparator']) => {
    const cells: string[] = [];
    let cell = '';
    let quoted = false;

    for (const character of line) {
        if (character === '"') quoted = !quoted;

        if (character === separator && !quoted) {
            cells.push(cell);
            cell = '';
        } else {
            cell += character;
        }
    }

    return [...cells, cell];
};

export type { CsvFormat };
export { CSV_FORMATS, CsvHelpers };
